import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Sidebar.css';

interface SidebarProps {
  activeSection: string;
  onSectionChange: (section: string) => void;
  projectName?: string;
  userRole?: 'admin' | 'member' | string;
}

interface NavItem {
  id: string;
  label: string;
  icon: string;
  description: string;
  adminOnly?: boolean;
  route?: string; // Optional route for page navigation
}

const navItems: NavItem[] = [
  {
    id: 'questionnaires',
    label: 'Questionnaires',
    icon: '📋',
    description: 'Manage async questionnaire processing',
    route: '/questionnaires'
  },
  {
    id: 'questionnaire',
    label: 'Quick Upload',
    icon: '📤',
    description: 'Upload and process compliance questionnaires (sync)'
  },
  {
    id: 'knowledge-base',
    label: 'Knowledge Base',
    icon: '📚',
    description: 'Manage your compliance knowledge repository'
  },
  {
    id: 'team',
    label: 'Team',
    icon: '👥',
    description: 'Manage team members and invitations',
    adminOnly: true
  },
  {
    id: 'subscription',
    label: 'Subscription',
    icon: '💳',
    description: 'Manage your subscription plan',
    adminOnly: true
  },
  {
    id: 'integrations',
    label: 'Integrations',
    icon: '🔗',
    description: 'Connect Slack and other integrations',
    adminOnly: true
  },
  {
    id: 'settings',
    label: 'Project Settings',
    icon: '⚙️',
    description: 'Configure project settings and preferences',
    adminOnly: true
  }
];

const Sidebar: React.FC<SidebarProps> = ({ activeSection, onSectionChange, projectName, userRole }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = userRole === 'admin';
  const visibleItems = navItems.filter(item => !item.adminOnly || isAdmin);

  const handleNavClick = (item: NavItem) => {
    if (item.route) {
      navigate(item.route);
    } else {
      // Navigate to dashboard if not already there
      if (!location.pathname.startsWith('/dashboard')) {
        navigate('/dashboard');
      }
      onSectionChange(item.id);
    }
  };

  const isActive = (item: NavItem) => {
    if (item.route) {
      return location.pathname.startsWith(item.route);
    }
    return location.pathname === '/dashboard' && activeSection === item.id;
  };

  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        <div className="sidebar-logo">
          <svg className="logo-icon-svg" width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="32" height="32" rx="8" fill="url(#logo-gradient-sidebar)"/>
            <path d="M10 16L14 20L22 12" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            <defs>
              <linearGradient id="logo-gradient-sidebar" x1="0" y1="0" x2="32" y2="32">
                <stop stopColor="#6366f1"/>
                <stop offset="1" stopColor="#8b5cf6"/>
              </linearGradient>
            </defs>
          </svg>
          <span className="logo-title">ComplyDeck</span>
        </div>
      </div>

      {projectName && (
        <div className="sidebar-project">
          <span className="project-label">Current Project</span>
          <span className="project-name">{projectName}</span>
        </div>
      )}

      <nav className="sidebar-nav">
        <ul className="nav-list">
          {visibleItems.map((item) => (
            <li key={item.id}>
              <button
                className={`nav-item ${isActive(item) ? 'active' : ''}`}
                onClick={() => handleNavClick(item)}
                title={item.description}
              >
                <span className="nav-icon">{item.icon}</span>
                <span className="nav-label">{item.label}</span>
              </button>
            </li>
          ))}
        </ul>
      </nav>

      <div className="sidebar-footer">
        <div className="sidebar-version">v1.0.0</div>
      </div>
    </aside>
  );
};

export default Sidebar;

